import { useState, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { SEOHead, generateBreadcrumbSchema, generateFAQSchema } from '@/components/base/SEO';
import { siteInfo } from '@/mocks/site';

function ContactForm() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormError('');
      form.reset();
      setCharCount(0);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pc54iftmtnfcfh0keg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  }, []);

  return (
    <div id="contact-form" className="bg-background-50 rounded-lg border border-background-200/70 p-6">
      <h3 className="text-lg font-semibold text-foreground-950 mb-4">发送消息</h3>

      {formStatus === 'success' && (
        <div className="mb-4 p-4 rounded-md bg-accent-100 text-accent-700 text-sm">
          <i className="ri-check-line mr-1"></i>消息已成功发送！感谢您的留言，我们会尽快回复。
        </div>
      )}

      {formStatus === 'error' && formError && (
        <div className="mb-4 p-4 rounded-md bg-red-50 text-red-700 text-sm">
          <i className="ri-error-warning-line mr-1"></i>{formError}
        </div>
      )}

      <form
        id="about-contact-form"
        data-readdy-form
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
            <input
              type="text"
              id="contact-name"
              name="name"
              required
              className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200/70 bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
              placeholder="请输入您的姓名"
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
            <input
              type="email"
              id="contact-email"
              name="email"
              required
              className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200/70 bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
              placeholder="请输入您的邮箱"
            />
          </div>
        </div>
        <div>
          <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1">主题</label>
          <input
            type="text"
            id="contact-subject"
            name="subject"
            required
            className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200/70 bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
            placeholder="请输入消息主题"
          />
        </div>
        <div>
          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">留言内容</label>
          <textarea
            id="contact-message"
            name="message"
            required
            rows={5}
            maxLength={500}
            onChange={(e) => setCharCount(e.target.value.length)}
            className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200/70 bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors resize-none"
            placeholder="请输入您的留言内容（500字以内）"
          ></textarea>
          <p className="text-xs text-foreground-400 mt-1">已输入 {charCount}/500 字</p>
        </div>

        <div className="form-honeypot-wrapper" aria-hidden="true">
          <input
            type="text"
            name="phone_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="form-honeypot-input"
          />
        </div>

        <button
          type="submit"
          disabled={formStatus === 'submitting'}
          className="w-full py-3 rounded-md bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-sm disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {formStatus === 'submitting' ? '发送中...' : '发送消息'}
        </button>
      </form>
    </div>
  );
}

export default function About() {
  const canonicalUrl = `${siteInfo.url}/about`;
  const breadcrumbItems = [
    { name: '首页', url: siteInfo.url },
    { name: '关于我们', url: canonicalUrl },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 - 品牌介绍、口碑评价与联系方式"
        description={`${siteInfo.name} - ${siteInfo.description} 了解我们的品牌故事、用户口碑评价，以及如何联系我们。`}
        keywords={`关于韩漫网,韩国漫画平台,联系韩漫网,${siteInfo.keywords}`}
        canonicalUrl={canonicalUrl}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify([
          generateBreadcrumbSchema(breadcrumbItems),
          generateFAQSchema(siteInfo.faqs),
        ]) }}
      />

      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          <main className="flex-1">
            {/* Brand Intro Hero */}
            <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
              <div className="text-center mb-12">
                <h1 className="text-3xl md:text-4xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  关于<span className="text-primary-500">韩漫网</span>
                </h1>
                <p className="text-foreground-500 mt-3 max-w-2xl mx-auto">
                  {siteInfo.slogan}
                </p>
              </div>

              <div className="flex flex-col md:flex-row gap-10 items-center">
                <div className="md:w-1/2">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20cozy%20creative%20office%20workspace%20with%20comic%20books%20and%20digital%20tablets%20on%20wooden%20desk%2C%20warm%20natural%20lighting%2C%20minimalist%20Korean%20aesthetic%2C%20comfortable%20reading%20atmosphere%2C%20clean%20professional%20composition%2C%20soft%20coral%20and%20cream%20color%20palette&width=640&height=420&seq=about-brand&orientation=landscape"
                    alt="韩漫网品牌形象"
                    className="w-full rounded-lg object-cover object-top"
                    width={640}
                    height={420}
                  />
                </div>
                <div className="md:w-1/2">
                  <h2 className="text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                    品牌故事
                  </h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    韩漫网成立于2020年，是国内领先的韩国漫画在线阅读平台。我们致力于为中文读者提供最全面、最新、最高质量的韩国漫画资源。
                  </p>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    我们与韩国多家顶级漫画工作室和出版社建立了深度合作关系，所有漫画均为正版授权。平台上的每一部作品都经过精心筛选和高质量翻译，确保读者能够享受到原汁原味的韩国漫画。
                  </p>
                  <p className="text-foreground-600 leading-relaxed">
                    <strong>{siteInfo.about.mission}</strong>
                  </p>
                </div>
              </div>
            </section>

            {/* Stats / Reputation */}
            <section className="bg-background-100 py-12 md:py-16">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <div className="text-center mb-10">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                    口碑与数据
                  </h2>
                  <p className="text-foreground-500 mt-2">数百万漫画爱好者的共同选择</p>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                  {siteInfo.about.reputation.map((item, idx) => (
                    <div key={idx} className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                      <div className="text-3xl md:text-4xl font-bold text-primary-500" style={{ fontFamily: 'var(--font-heading)' }}>
                        {item.stat}
                      </div>
                      <div className="text-sm text-foreground-500 mt-2">{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Core Values */}
            <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  我们的优势
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="p-6 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                    <i className="ri-shield-check-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">正版授权</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    所有漫画均获得韩国版权方的正版授权，严格遵守知识产权保护法规，为读者提供合法优质的阅读体验。
                  </p>
                </div>

                <div className="p-6 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                    <i className="ri-image-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">高清画质</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    所有漫画均采用高清数字原版，画质清晰细腻，支持多端适配，让您在任意设备上都能享受极致视觉体验。
                  </p>
                </div>

                <div className="p-6 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                    <i className="ri-flashlight-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">极速更新</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    紧跟韩国更新节奏，热门作品每周稳定更新。专业翻译团队高效运作，确保第一时间同步上线。
                  </p>
                </div>

                <div className="p-6 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                    <i className="ri-emotion-happy-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">完全免费</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    坚持免费阅读模式，不设付费墙。通过广告收入维持平台运营，让每位读者都能自由畅享漫画世界。
                  </p>
                </div>

                <div className="p-6 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                    <i className="ri-global-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">全终端覆盖</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    完美适配PC、平板和手机端，无需下载APP即可畅快阅读。阅读进度云端同步，随时随地无缝衔接。
                  </p>
                </div>

                <div className="p-6 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                    <i className="ri-heart-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">用户至上</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    重视每一位读者的反馈，持续优化阅读体验。建立完善的用户服务体系，及时响应和解决使用问题。
                  </p>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section className="bg-background-100 py-12 md:py-16">
              <div className="max-w-3xl mx-auto px-4 md:px-6">
                <div className="text-center mb-10">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                    常见问题
                  </h2>
                  <p className="text-foreground-500 mt-2">您可能关心的问题</p>
                </div>

                <div className="space-y-3">
                  {siteInfo.faqs.map((faq, idx) => (
                    <details key={idx} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                        <span className="text-sm font-medium text-foreground-950 pr-4">{faq.question}</span>
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0 w-5 h-5 flex items-center justify-center"></i>
                      </summary>
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
              <div className="max-w-2xl mx-auto">
                <div className="text-center mb-10">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                    联系我们
                  </h2>
                  <p className="text-foreground-500 mt-2">有任何问题或合作意向，欢迎联系我们</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                  <div className="text-center p-5 bg-background-50 rounded-lg border border-background-200/70">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                      <i className="ri-mail-line text-lg text-primary-500"></i>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h4>
                    <p className="text-sm text-foreground-500">{siteInfo.email}</p>
                  </div>
                  <div className="text-center p-5 bg-background-50 rounded-lg border border-background-200/70">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                      <i className="ri-phone-line text-lg text-primary-500"></i>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-950 mb-1">客服电话</h4>
                    <p className="text-sm text-foreground-500">{siteInfo.phone}</p>
                  </div>
                  <div className="text-center p-5 bg-background-50 rounded-lg border border-background-200/70">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                      <i className="ri-map-pin-line text-lg text-primary-500"></i>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-950 mb-1">公司地址</h4>
                    <p className="text-sm text-foreground-500">{siteInfo.address}</p>
                  </div>
                </div>

                <ContactForm />
              </div>
            </section>
          </main>
        </div>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}